/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import PropTypes from "prop-types"; // Npm: react.js library.
import JoiBrowser from "joi-browser"; // Npm: Joi for frontend validation.
import _ from "underscore"; // Npm: Underscore.js library.
import { toast } from "react-hot-toast"; // Npm: React hot toast.
import { connect } from "react-redux"; // Npm: React Redux for state management.
import { useLazyQuery, useMutation, useQuery } from "@apollo/client"; // Npm: Apollo client.
import { Checkbox, Flex, Text, useBreakpointValue } from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import SubmitButton from "components/SubmitButton";
import { MemoizedInput, MemoizedSelect } from "components/MemoizedInput";

/*
 * GRAPHS
 */
import RateReadQuery from "./__query__/index.rate.read.query";

import RouteListQuery from "./__query__/index.route.list.query";
import CustomerAccountAllQuery from "./__query__/index.customerAccount.read.all.query";
import CustomerAccountAttachRateMutation from "./__mutation__/index.customerAccount.attachRate.mutation";
import CustomerAccountAttachRouteMutation from "./__mutation__/index.customerAccount.attachRoute.mutation";
import SmppUpdateMutation from "./__mutation__/index.smpp.update.mutation";
import SmppMultiUpdateMutation from "./__mutation__/index.smpp.multi.update.mutation";
import SmppMultiCreateMutation from "./__mutation__/index.smpp.multi.create.mutation";

// generate batch id
const generateBatchId = () => {
  let batchId = "";
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  for (let i = 0; i < 24; i++) {
    batchId += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return batchId;
};
/*
 * OBJECTS
 */
const Index = ({ isCreateOnly, isOpen, onClose, refetch, passOn }) => {
  // Const assignment.
  const _skipDifference = 8;

  // Hook assignment.
  const [rate, setRate] = React.useState("");
  const [route, setRoute] = React.useState("");
  const [originalUsername, setOriginalUsername] = React.useState(""); //track original username
  const [
    MutationCustomerAccountAttachRoute,
    MutationCustomerAccountAttachRouteResponse,
  ] = useMutation(CustomerAccountAttachRouteMutation);
  const [
    MutationCustomerAccountAttachRate,
    MutationCustomerAccountAttachRateResponse,
  ] = useMutation(CustomerAccountAttachRateMutation);
  const [QueryCustomerAccountRead, QueryCustomerAccountReadResponse] =
    useLazyQuery(CustomerAccountAllQuery);
  const [error, setError] = React.useState("");
  const [formVersion, _ResetFormInputs] = React.useReducer((j) => j + 1, 0);
  const [, _TriggerRender] = React.useReducer((j) => j + 1, 0);
  const [MutationSmppMultiCreate, MutationSmppMultiCreateResponse] =
    useMutation(SmppMultiCreateMutation);
  const [MutationSmppUpdate, MutationSmppUpdateResponse] =
    useMutation(SmppUpdateMutation);
  const [MutationSmppMultiUpdate, MutationSmppMultiUpdateResponse] =
    useMutation(SmppMultiUpdateMutation);

  const _RateReadQuery = useQuery(RateReadQuery, {
    variables: { type: passOn?.type, take: _skipDifference, skip: 0 },
    fetchPolicy: Object.React.App.fetchPolicy,
    skip: !isOpen,
  });
  const _RouteListQuery = useQuery(RouteListQuery, {
    variables: { type: passOn?.type, take: _skipDifference, skip: 0 },
    fetchPolicy: Object.React.App.fetchPolicy,
    skip: !isOpen,
  });
  const _formDataRef = React.useRef({
    enableLossProtection: false,
    chargeOnDelivered: false,
  });
  const _isCurrentViewMobile = useBreakpointValue({
    base: false,
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  // Object assignment.
  const _SubmitForm = async (e) => {
    // Local variable.
    let _MutationCustomerAccountAttachRateToCustomer,
      _MutationCustomerAccountAttachRouteToCustomer;

    // Prevent default behavior.
    e?.preventDefault();

    // Reset error.
    setError("");

    // Object assignment.
    const _CustomerAccountAttachRouteAndRate = async (__customerAccountId) => {
      // Const assignment.
      const _route =
        route?.includes?.("(") && route?.includes?.(")")
          ? route?.split("(")[1]?.split(")")[0]
          : route;
      const _rate =
        rate?.includes?.("(") && rate?.includes?.(")")
          ? rate?.split("(")[1]?.split(")")[0]
          : rate;

      // Only Execute if route has data.
      if (!_.isEmpty(_route)) {
        // Execute mutation.
        _MutationCustomerAccountAttachRouteToCustomer =
          await MutationCustomerAccountAttachRoute({
            variables: {
              customerAccountId:
                __customerAccountId ?? passOn?.customerAccountId,
              routeId: _route,
            },
          });

        // If mutation caught an exception then report failure.
        if (_MutationCustomerAccountAttachRouteToCustomer instanceof Error)
          throw _MutationCustomerAccountAttachRouteToCustomer;
      }

      // Only proceed if rate has data.
      if (!_.isEmpty(_rate)) {
        // Bind Rate to given customer.
        _MutationCustomerAccountAttachRateToCustomer =
          await MutationCustomerAccountAttachRate({
            variables: {
              customerAccountId:
                __customerAccountId ?? passOn?.customerAccountId,
              rateId: _rate,
            },
          });
        // If mutation caught an exception then report failure.
        if (_MutationCustomerAccountAttachRateToCustomer instanceof Error)
          throw _MutationCustomerAccountAttachRateToCustomer;
      }
    };

    // Const assignment.
    const _JoiSchema = JoiBrowser.object({
      displayName: JoiBrowser.string().required(),
      // 'ip': isCreateOnly || passOn?.isMulti ? JoiBrowser.array().items(JoiBrowser.string().max(45).ip({ version: ['ipv4', 'ipv6', 'ipvfuture'], cidr: 'optional' }).required()).unique().required() : JoiBrowser.string().max(45).ip({ version: ['ipv4', 'ipv6', 'ipvfuture'], cidr: 'optional' }).required(),
      ip:
        isCreateOnly || passOn?.isMulti
          ? JoiBrowser.array()
              .items(
                JoiBrowser.alternatives()
                  .try(
                    JoiBrowser.string()
                      .max(45)
                      .ip({
                        version: ["ipv4", "ipv6", "ipvfuture"],
                        cidr: "optional",
                      }),
                    JoiBrowser.string()
                      .max(253)
                      .regex(
                        /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/,
                        "domain name",
                      ),
                  )
                  .required(),
              )
              .unique()
              .required()
          : JoiBrowser.alternatives()
              .try(
                JoiBrowser.string()
                  .max(45)
                  .ip({
                    version: ["ipv4", "ipv6", "ipvfuture"],
                    cidr: "optional",
                  }),
                JoiBrowser.string()
                  .max(253)
                  .regex(
                    /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/,
                    "domain name",
                  ),
              )
              .required(),
      username: JoiBrowser.string().required(),
      password: JoiBrowser.string().required(),
      sessionTimeoutInterval: JoiBrowser.number().required(),
      sessionAllowed: JoiBrowser.number().optional(),
      tps: JoiBrowser.number().optional(),
      enquireLinkInterval: JoiBrowser.number().required(),
      sourceAddressTon: JoiBrowser.number().optional(),
      sourceAddressNpi: JoiBrowser.number().optional(),
      destinationAddressTon: JoiBrowser.number().optional(),
      destinationAddressNpi: JoiBrowser.number().optional(),
      trunkType: JoiBrowser.string().required(),
      debuggingLevel: JoiBrowser.any().required(),
      rate: JoiBrowser.string().required(),
      routePlan: JoiBrowser.string().required(),
      enableLossProtection: JoiBrowser.boolean().optional(),
      chargeOnDelivered: JoiBrowser.boolean().optional(),
    }).options({ allowUnknown: true });

    // Remove all keys from _formDataRef.current which are undefined.
  _formDataRef.current = _.pick(
    _formDataRef.current,
    (value) => value !== undefined && value !== null,
  );

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate(_formDataRef.current);

    // If error exists then report failure.
    if (_JoiSchemaValidate.error)
      return setError(_JoiSchemaValidate.error?.message);

    const _MutationSmppUpdate = await [
      isCreateOnly
        ? MutationSmppMultiCreate
        : passOn?.isMulti
          ? MutationSmppMultiUpdate
          : MutationSmppUpdate,
    ]?.[0]({
      variables: {
        // Pass customerId for create, and also when isMulti is true; otherwise skip it
        customerId: isCreateOnly
          ? passOn?.customerId
          : passOn?.isMulti
            ? passOn?.customerId
            : undefined,

        ...(!isCreateOnly &&
          passOn?.isMulti && {
            customerAccountIds: passOn?.customerAccountIds || [],
            smppIds: _formDataRef?.current?.smppIds || [],
          }),
        // Remove smppId from formDataRef when creating, otherwise keep it as is for update
        ..._.omit(
          _formDataRef?.current,
          isCreateOnly ? "smppId" : undefined,
          "enableLossProtection",
          "chargeOnDelivered",
          "smppIds",
        ),

        smppId: isCreateOnly
          ? undefined
          : passOn?.isMulti
            ? undefined
            : _formDataRef.current.smppId,

        // Always provide oldUsername for SmppMultiUpdate (when passOn?.isMulti is true)
        ...(!isCreateOnly &&
          passOn?.isMulti && {
            oldUsername: originalUsername || _formDataRef.current.username,
          }),

        ip: isCreateOnly
          ? Array.isArray(_formDataRef.current.ip)
            ? _formDataRef.current.ip
            : String(_formDataRef.current.ip || "")
                .split(",")
                .map((ip) => ip.trim())
                .filter((ip) => ip.length > 0)
          : _formDataRef.current.ip,

        // Explicitly pass enableLossProtection for all operations
        enableLossProtection: Boolean(
          _formDataRef?.current?.enableLossProtection,
        ),
        chargeOnDelivered: Boolean(_formDataRef?.current?.chargeOnDelivered),
        batchId: isCreateOnly
          ? generateBatchId()
          : _formDataRef.current.batchId || generateBatchId(), // If it’s not in the form, then generate a new one
      },
    });

    // If mutation caught an exception then report failure.
    if (_MutationSmppUpdate instanceof Error) return _MutationSmppUpdate;

    if (
      _MutationSmppUpdate?.data?.SmppMultiCreate?.data?.status ===
      "EXCEPTION_CAUGHT"
    ) {
      toast(_MutationSmppUpdate?.data?.SmppMultiCreate?.data?.message);
      return;
    }
    // Style Guide.
    toast(
      _MutationSmppUpdate?.data?.SmppMultiCreate?.message ??
        _MutationSmppUpdate?.data?.SmppUpdate?.message ??
        _MutationSmppUpdate?.data?.SmppMultiUpdate?.message,
    );
    /*
     * Only execute attach query if its about
     * creation of new smpp.
     */

    if (
      isCreateOnly &&
      "CREATE_SUCCESSFUL" === _MutationSmppUpdate?.data?.SmppMultiCreate?.status
    ) {
      // Get all created accounts

      const createdAccounts = Array.isArray(
        _MutationSmppUpdate?.data?.SmppMultiCreate?.data,
      )
        ? _MutationSmppUpdate?.data?.SmppMultiCreate?.data // use array if avaialble
        : []; // else empty array

      //  Parallel async operations
      await Promise.all(
        createdAccounts
          .filter((account) => account?.customerAccountId)
          .map((account) =>
            _CustomerAccountAttachRouteAndRate(account.customerAccountId),
          ),
      );

      await refetch?.();
      return onClose?.();
    }

    //  Close modal after successful update operation
    if (
      "UPDATE_SUCCESSFUL" === _MutationSmppUpdate?.data?.SmppUpdate?.status ||
      "UPDATE_SUCCESSFUL" === _MutationSmppUpdate?.data?.SmppMultiUpdate?.status
    ) {
      // Get all created accounts
      const UpdateAccounts = Array.isArray(
        _MutationSmppUpdate?.data?.SmppMultiUpdate?.data,
      )
        ? _MutationSmppUpdate?.data?.SmppMultiUpdate?.data
        : [];

      const accountIds = UpdateAccounts.map((item) => item.customerAccountId);
      const mergedIds = Array.from(
        new Set([...(passOn?.customerAccountIds || []), ...accountIds]),
      );

      // Parallel async operations
      await Promise.all(
        mergedIds
          .filter((id) => id)
          .map((id) => _CustomerAccountAttachRouteAndRate(id)),
      );

      await refetch?.();
      return onClose?.();
    }

    // Return void 0.
    return void 0;
  };

  //event handler
  React.useEffect(() => {
    // Only load form data while modal is open to avoid duplicate API calls.
    if (!isOpen) return;

    // Create mode does not need a read query; reset local form state only.
    if (isCreateOnly) {
      _formDataRef.current = {
        enableLossProtection: false,
        chargeOnDelivered: false,
        sessionTimeoutInterval: 30000,
        enquireLinkInterval: 30000,
      };
      setRate("");
      setRoute("");
      setOriginalUsername("");
      setError("");
      _ResetFormInputs();
      return;
    }

    // Skip if update mode but no account IDs provided
    if (
      !isCreateOnly &&
      !passOn?.customerAccountId &&
      !passOn?.customerAccountIds?.length
    )
      return;

    let _isActive = true;

    // _Async handler.
    const _Async = async () => {
      const _QueryCustomerAccountReadQuery = await QueryCustomerAccountRead({
        variables: {
          customerAccountIds: passOn?.customerAccountIds || [
            passOn?.customerAccountId,
          ],
        },
        fetchPolicy: Object.React.App.fetchPolicy,
      });

      if (!_isActive) return void 0;

      // If query caught an exception then report failure.
      if (_QueryCustomerAccountReadQuery instanceof Error)
        return _QueryCustomerAccountReadQuery;

      /*
       * If details fetch complete then
       * update its value.
       */
      if (
        _.first(
          _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
        ) &&
        !_.isEmpty(
          _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp,
        )
      ) {
        // Store original username for update operations
        if (
          !isCreateOnly &&
          _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          )?.Smpp?.username
        ) {
          setOriginalUsername(
            _.first(
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
                _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
            ).Smpp.username,
          );
        }

        // Use batchId from response, or generate a new one if missing
        const responseBatchId =
          _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          )?.batchId ||
          _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          )?.Smpp?.batchId ||
          generateBatchId(); //Generate a new one if it doesn't exist

        let ipArray = [];

        if (passOn?.isMulti) {
          // For batch updates, collect all IPs from all records
          ipArray = (
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll
          )
            .map((record) => record.Smpp?.ip)
            .filter((ip) => ip)
            .flatMap((ip) => (Array.isArray(ip) ? ip : [ip]));
        } else if (passOn?.mergedIPs) {
          // Use mergedIPs string if available
          ipArray = passOn.mergedIPs
            .split(",")
            .map((ip) => ip.trim())
            .filter((ip) => ip.length > 0);
        } else {
          // Fallback to Smpp.ip from API, normalize to array
          const ipFromApi = _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp.ip;
          if (Array.isArray(ipFromApi)) {
            ipArray = ipFromApi;
          } else if (typeof ipFromApi === "string") {
            ipArray = ipFromApi
              .split(",")
              .map((ip) => ip.trim())
              .filter((ip) => ip.length > 0);
          }
        }

        // Update form data.
        _formDataRef.current = {
          displayName: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).displayName,
          ip: passOn?.isMulti
            ? ipArray
            : _.first(
                _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
                  _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
              ).Smpp.ip,
          username: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp.username,
          password: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp.password,
          sessionTimeoutInterval: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp.sessionTimeoutInterval,
          sessionAllowed: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp.sessionAllowed,
          tps: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp.tps,
          enquireLinkInterval: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp.enquireLinkInterval,
          sourceAddressTon: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp.sourceAddressTon,
          sourceAddressNpi: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp.sourceAddressNpi,
          destinationAddressTon: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp.destinationAddressTon,
          destinationAddressNpi: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp.destinationAddressNpi,
          trunkType: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp.trunkType,
          debuggingLevel: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp.debuggingLevel,
          rate: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Rate?.displayName
            ? `${_.first(_QueryCustomerAccountReadQuery?.data?.CustomerAccountRead || _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll).Rate?.displayName} (${_.first(_QueryCustomerAccountReadQuery?.data?.CustomerAccountRead || _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll).Rate?.id})`
            : void 0,
          routePlan: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Route?.displayName
            ? `${_.first(_QueryCustomerAccountReadQuery?.data?.CustomerAccountRead || _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll).Route?.displayName} (${_.first(_QueryCustomerAccountReadQuery?.data?.CustomerAccountRead || _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll).Route?.id})`
            : void 0,
          enableLossProtection: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          )?.enableLossProtection,
          chargeOnDelivered: _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          )?.chargeOnDelivered,
          batchId: responseBatchId,
        };
        if (!passOn?.isMulti) {
          _formDataRef.current.smppId = _.first(
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
              _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll,
          ).Smpp.id;
        }
        // Extract SMPP IDs from response for multi update
        if (passOn?.isMulti) {
          _formDataRef.current.smppIds = (
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountRead ||
            _QueryCustomerAccountReadQuery?.data?.CustomerAccountReadAll
          )
            .map((account) => account?.Smpp?.id)
            .filter((id) => id);
        }
        // Keep existing rate/route visible, but only reattach when user changes them.
        setRate("");
        setRoute("");

        // Reset uncontrolled inputs once loaded data is ready.
        return _ResetFormInputs();
      }

      // Report failure.
      return void 0;
    };
    _Async();

    return () => {
      _isActive = false;
    };
  }, [
    QueryCustomerAccountRead,
    _ResetFormInputs,
    isCreateOnly,
    isOpen,
    passOn?.customerAccountId,
    passOn?.customerAccountIds,
    passOn?.isMulti,
    passOn?.mergedIPs,
  ]);

  // Updated _isInputDisabled logic for conditional field disabling
  const _isLoading =
    MutationCustomerAccountAttachRateResponse.loading ||
    MutationCustomerAccountAttachRouteResponse.loading ||
    MutationSmppMultiCreateResponse.loading ||
    MutationSmppUpdateResponse.loading ||
    MutationSmppMultiUpdateResponse.loading;

  const _isInputDisabled = isCreateOnly
    ? false
    : _isLoading || QueryCustomerAccountReadResponse?.loading;

  const isFieldDisabled = React.useMemo(() => {
    return (fieldName) => {
      if (isCreateOnly) return false;

      if (passOn?.isExpandedIndividual) {
        if (fieldName === "ip" || fieldName === "tps") {
          return _isInputDisabled;
        } else {
          return true;
        }
      }

      return _isInputDisabled;
    };
  }, [isCreateOnly, passOn?.isExpandedIndividual, _isInputDisabled]);

  // Return component.
  return (
    <form onSubmit={_SubmitForm} className="customerAccountUpsert">
      <Flex gap="22px" flexDir="column" w="100%">
        <Flex w="100%" gap="22px" flexDir={{ base: "column", md: "row" }}>
          <MemoizedInput
            disabled={isFieldDisabled("displayName")}
            isRequired={true}
            name="displayName"
            label="Display name"
            placeholder='e.g. "Rocking Smpp"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target;

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: value,
              };
            }}
            isInvalid={error?.includes("displayName")}
            error={error}
            data={_formDataRef?.current?.displayName}
          />
          <MemoizedInput
            disabled={isFieldDisabled("ip")}
            name="ip"
            label="ip"
            placeholder='e.g. "0.0.0.0"'
            onChange={({ target }) => {
              const { name, value } = target;

              if (isCreateOnly || passOn?.isMulti) {
                // Check if value is already an array
                const ipArray = Array.isArray(value)
                  ? value
                  : value
                      .split(",")
                      .map((ip) => ip.trim())
                      .filter(Boolean);

                _formDataRef.current = {
                  ..._formDataRef?.current,
                  [name]: ipArray,
                };

                // Validate only if multiple IPs exist
                if (
                  ipArray.length > 1 &&
                  ipArray.length !== new Set(ipArray).size
                ) {
                  setError("Duplicate IPs not allowed");
                } else if (error?.includes("Duplicate")) {
                  setError("");
                }
              } else {
                _formDataRef.current = {
                  ..._formDataRef?.current,
                  [name]: value.trim(),
                };
              }
            }}
            isRequired={true}
            separators={[",", "Enter", "Tab"]}
            isMultiple={isCreateOnly || passOn?.isMulti}
            error={error}
            isInvalid={error?.includes("ip") || error?.includes("Duplicate")}
            data={_formDataRef?.current?.ip}
          />
        </Flex>
        <Flex w="100%" gap="22px" flexDir={{ base: "column", md: "row" }}>
          <MemoizedInput
            disabled={isFieldDisabled("username")}
            name="username"
            label="SystemId"
            placeholder='e.g. "RockSmpp"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target;

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: value,
              };
            }}
            isRequired={true}
            error={error}
            isInvalid={error?.includes("username")}
            data={_formDataRef?.current?.username}
          />
          <MemoizedInput
            disabled={isFieldDisabled("password")}
            name="password"
            label="Password"
            placeholder='e.g. "*******"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target;

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: value,
              };
            }}
            isRequired={true}
            error={error}
            isInvalid={error?.includes("password")}
            data={_formDataRef?.current?.password}
          />
        </Flex>
        <Flex w="100%" gap="22px" flexDir={{ base: "column", md: "row" }}>
          <MemoizedInput
            disabled={isFieldDisabled("sessionTimeoutInterval")}
            isRequired={true}
            name="sessionTimeoutInterval"
            label="Response Time ( in Seconds )"
            placeholder='e.g. "3 is 3second"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target;

              // Make sure that value is in seconds.
              if (1 > Number(value))
                return setError(
                  "sessionTimeoutInterval must be greater than 0",
                );

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value ?? 0) * 1000,
              };

              // Return void.
              return void 0;
            }}
            error={error}
            isInvalid={error?.includes("sessionTimeoutInterval")}
            data={
              _.isNumber(_formDataRef?.current?.enquireLinkInterval) &&
              0 < _formDataRef?.current?.sessionTimeoutInterval
                ? _formDataRef?.current?.sessionTimeoutInterval / 1000
                : _formDataRef?.current?.sessionTimeoutInterval
            }
          />
          <MemoizedSelect
            disabled={isFieldDisabled("Session Allowed")}
            name="sessionAllowed"
            label="Session Allowed"
            placeholder='e.g. "2"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target;

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value ?? 0),
              };

              // Force re-render.
              _TriggerRender();
            }}
            options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
            error={error}
            isInvalid={error?.includes("sessionAllowed")}
            value={_formDataRef?.current?.sessionAllowed}
          />
        </Flex>
        <Flex w="100%" gap="22px" flexDir={{ base: "column", md: "row" }}>
          <MemoizedInput
            disabled={isFieldDisabled("tps")}
            name="tps"
            label="Submit Per Second"
            placeholder='e.g. "1000 is 1000 Submit Per Second"'
            onChange={({ target }) => {
              const { name, value } = target;
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: 1000 < Number(value ?? 0) ? 1000 : Number(value ?? 0),
              };
            }}
            error={error}
            isInvalid={error?.includes("tps")}
            data={_formDataRef?.current?.tps}
          />
          <MemoizedInput
            disabled={isFieldDisabled("enquireLinkInterval")}
            isRequired={true}
            name="enquireLinkInterval"
            label="Enquire link ( in Seconds )"
            placeholder='e.g. "60 is 60Seconds"'
            onChange={({ target }) => {
              const { name, value } = target;

              if (1 > Number(value))
                return setError("enquireLinkInterval must be greater than 0");

              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value ?? 0) * 1000,
              };

              return void 0;
            }}
            error={error}
            isInvalid={error?.includes("enquireLinkInterval")}
            data={
              _.isNumber(_formDataRef?.current?.enquireLinkInterval) &&
              0 < _formDataRef?.current?.enquireLinkInterval
                ? _formDataRef?.current?.enquireLinkInterval / 1000
                : _formDataRef?.current?.enquireLinkInterval
            }
          />
        </Flex>
        <Flex w="100%" gap="22px" flexDir={{ base: "column", md: "row" }}>
          <MemoizedInput
            disabled={isFieldDisabled("sourceAddressTon")}
            name="sourceAddressTon"
            label="SourceAddress ton"
            placeholder='e.g. "0"'
            onChange={({ target }) => {
              const { name, value } = target;

              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value),
              };
            }}
            error={error}
            isInvalid={error?.includes("sourceAddressTon")}
            data={_formDataRef?.current?.sourceAddressTon}
          />
          <MemoizedInput
            disabled={isFieldDisabled("sourceAddressNpi")}
            name="sourceAddressNpi"
            label="SourceAddress npi"
            placeholder='e.g. "0"'
            onChange={({ target }) => {
              const { name, value } = target;

              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value),
              };
            }}
            error={error}
            isInvalid={error?.includes("sourceAddressNpi")}
            data={_formDataRef?.current?.sourceAddressNpi}
          />
        </Flex>
        <Flex w="100%" gap="22px" flexDir={{ base: "column", md: "row" }}>
          <MemoizedInput
            disabled={isFieldDisabled("destinationAddressTon")}
            name="destinationAddressTon"
            label="DestinationAddress Ton"
            placeholder='e.g. "1"'
            onChange={({ target }) => {
              const { name, value } = target;

              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value),
              };
            }}
            error={error}
            isInvalid={error?.includes("destinationAddressTon")}
            data={_formDataRef?.current?.destinationAddressTon}
          />
          <MemoizedInput
            disabled={isFieldDisabled("destinationAddressNpi")}
            name="destinationAddressNpi"
            label="DestinationAddress npi"
            placeholder='e.g. "1"'
            onChange={({ target }) => {
              const { name, value } = target;

              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: Number(value),
              };
            }}
            error={error}
            isInvalid={error?.includes("destinationAddressNpi")}
            data={_formDataRef?.current?.destinationAddressNpi}
          />
        </Flex>
        <Flex w="100%" gap="22px" flexDir={{ base: "column", md: "row" }}>
          <MemoizedSelect
            disabled={isFieldDisabled("trunkType")}
            isRequired={true}
            name="trunkType"
            label="Route"
            placeholder="Select Route"
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target;

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: value,
              };

              // Force re-render.
              _TriggerRender();
            }}
            error={error}
            options={Object.React.App.enums.TRUNK_TYPE.enums?.map((i) => i.key)}
            isInvalid={error?.includes("trunkType")}
            value={_formDataRef?.current?.trunkType}
          />
          <MemoizedSelect
            disabled={isFieldDisabled("Error Code Level")}
            isRequired={true}
            name="debuggingLevel"
            label="Error Code Level"
            placeholder="Select Debugging level"
            onChange={({ target }) => {
              const { name, value } = target;

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: value,
              };

              // Force re-render.
              _TriggerRender();
            }}
            error={error}
            options={Object.React.App.enums.SMPP_DEBUGGING_LEVEL.enums?.map(
              (i) => i.key,
            )}
            isInvalid={error?.includes("debuggingLevel")}
            value={_formDataRef?.current?.debuggingLevel}
          />
        </Flex>
        <Flex
          w="100%"
          gap="22px"
          flexDir={{ base: "column", md: "row" }}
          key={formVersion}
        >
          <MemoizedSelect
            disabled={isFieldDisabled("rate")}
            isRequired={true}
            name="rate"
            label="Assign Rate"
            value={_formDataRef?.current?.rate ?? rate}
            placeholder={
              _RateReadQuery?.loading
                ? "Loading rates..."
                : 'e.g. "Awesome Rate"'
            }
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target;

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: value,
              };

              // Update rate.
              setRate(value);
            }}
            key={`rate-${formVersion}`}
            error={error}
            isInvalid={error?.includes("rate")}
            options={_RateReadQuery?.data?.RateRead?.map(
              ({ id, displayName }) => `${displayName} (${id})`,
            )}
          />
          <MemoizedSelect
            disabled={isFieldDisabled("routes")}
            isRequired={true}
            label="Assign Route"
            name="routePlan"
            value={_formDataRef?.current?.routePlan ?? route}
            placeholder='e.g. "Amazing Route"'
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target;

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: value,
              };

              // Update route.
              setRoute(value);
            }}
            key={`route-${formVersion}`}
            error={error}
            isInvalid={error?.includes("routePlan")}
            options={_RouteListQuery?.data?.RouteList?.map?.(
              ({ id, displayName }) => `${displayName} (${id})`,
            )}
          />
        </Flex>
      </Flex>
      <Flex
        flexDir="column"
        gap="20px"
        mb={_isCurrentViewMobile ? "12px" : 0}
        mt="18px"
      >
        <Flex flexDir="row" alignItems="flex-start" gap="32px">
          <Flex flexDir="column">
            <Checkbox
              colorScheme="red"
              color="red.500"
              name="enableLossProtection"
              disabled={isFieldDisabled("enableLossProtection")}
              isChecked={_formDataRef?.current?.enableLossProtection}
              onChange={({ target }) => {
                // Over spreading.
                const { name, checked } = target;

                // Update form data.
                _formDataRef.current = {
                  ..._formDataRef?.current,
                  [name]: checked,
                };

                // Force re-render.
                _TriggerRender();
              }}
            >
              <Text fontWeight={600}>Enable Loss Protection ?</Text>
            </Checkbox>
            <Text fontSize="14px" fontWeight="500" color="gray.500">
              All deliveries will <span style={{ fontWeight: 900 }}>FAIL</span>{" "}
              if <span style={{ fontWeight: 900 }}>OVERDRAFT</span> limit
              exceeds.
            </Text>
          </Flex>
        </Flex>
        <Flex flexDir="column" mb={_isCurrentViewMobile ? "12px" : 0}>
          <Checkbox
            colorScheme="red"
            color="red.500"
            name="chargeOnDelivered"
            disabled={isFieldDisabled("chargeOnDelivered")}
            isChecked={_formDataRef?.current?.chargeOnDelivered}
            onChange={({ target }) => {
              // Over spreading.
              const { name, checked } = target;

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: checked,
              };

              // Force re-render.
              _TriggerRender();
            }}
          >
            <Text fontWeight={600}>Charge On Delivery ?</Text>
          </Checkbox>
          <Text fontSize="14px" fontWeight="500" color="gray.500">
            Charge customer on delivery receipt.
          </Text>
        </Flex>
        <SubmitButton
          mt={-8}
          onClick={_SubmitForm}
          disabled={_isInputDisabled}
          isLoading={_isLoading}
          defaultText={isCreateOnly ? "Create Smpp" : "Update Smpp"}
        />
      </Flex>
    </form>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {
  passOn: PropTypes.object,
  onClose: PropTypes.func,
  refetch: PropTypes.func,
  isCreateOnly: PropTypes.bool,
  isOpen: PropTypes.bool,
};
Index.defaultProps = {};

/*
 * REDUX
 */
const _MapStateToProps = (__state) => ({ passOn: __state.PassOn });

/*
 * EXPORT
 */
export default connect(_MapStateToProps)(React.memo(Index));
